import { Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProjectService } from '@/core/services/project.service';
import { ProjectApiModel } from '@/core/models/api/project-api-model.model';
import { UserService } from '@/core/services/user.service';
import { ProjectLocalStorage } from '@/types/project';
import { finalize } from 'rxjs';

@Component({
    selector: 'app-project-selector',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectModule, ButtonModule, TooltipModule],
    template: `
        @if(selectedProject && projectList.length > 0) {
        <div [ngClass]="compact ? 'project-selector-compact' : 'project-selector-full'">
            <div [ngClass]="compact ? 'compact-wrapper' : 'full-wrapper'">
                @if(!compact) {
                <i class="pi pi-briefcase text-blue-400 text-sm"></i>
                <span class="text-xs font-semibold text-surface-600 dark:text-surface-300 hidden sm:inline">Proyecto:</span>
                }
                <p-select 
                    [(ngModel)]="selectedProjectId"
                    [options]="projectList" 
                    optionLabel="name" 
                    optionValue="projectId"
                    [filter]="true"
                    filterBy="name,code,businessCode,client.name"
                    placeholder="Seleccionar proyecto"
                    (onChange)="onProjectChange($event)"
                    [loading]="isLoadingProjects"
                    [styleClass]="compact ? 'compact-select' : 'w-full sm:w-[350px] md:w-[400px]'"
                    panelStyleClass="project-selector-panel">
                    <ng-template #item let-project>
                        <div class="flex items-center gap-3 py-2">
                            <div class="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-400/30 flex items-center justify-center flex-shrink-0">
                                <i class="pi pi-folder text-blue-400 text-base"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <div class="font-semibold text-surface-900 dark:text-surface-0 truncate text-sm">{{ project.name }}</div>
                                <div class="text-xs text-surface-500 dark:text-surface-400 truncate">
                                    {{ project.code }} • {{ project.client?.name }}
                                </div>
                            </div>
                        </div>
                    </ng-template>
                    <ng-template #value let-project>
                        @if(project) {
                        <div class="flex items-center gap-2">
                            <i class="pi pi-folder text-blue-400 text-sm"></i>
                            <span [ngClass]="compact ? 'font-medium text-sm truncate max-w-[150px] sm:max-w-[200px]' : 'font-semibold truncate max-w-[200px] sm:max-w-[280px]'" class="text-surface-900 dark:text-surface-0">{{ project.name }}</span>
                            @if(!compact) {
                            <i class="pi pi-chevron-down text-xs text-surface-400"></i>
                            }
                        </div>
                        }
                    </ng-template>
                </p-select>
                <button 
                    pButton 
                    type="button"
                    icon="pi pi-external-link" 
                    [text]="true"
                    [rounded]="true"
                    size="small"
                    severity="secondary"
                    [pTooltip]="compact ? 'Dashboard del proyecto' : 'Ir al dashboard del proyecto'"
                    tooltipPosition="bottom"
                    (click)="goToProjectDashboard()"
                    [ngClass]="compact ? '!w-7 !h-7 ml-1' : '!w-8 !h-8 flex-shrink-0'">
                </button>
            </div>
        </div>
        }
    `
})
export class AppProjectSelector implements OnInit {
    @Input() compact: boolean = false;
    
    selectedProject: ProjectLocalStorage | null = null;
    selectedProjectId: number | null = null;
    projectList: ProjectApiModel[] = [];
    isLoadingProjects: boolean = false;

    private projectService = inject(ProjectService);
    private userService = inject(UserService);
    private router = inject(Router);

    ngOnInit(): void {
        this.userService.selectedProject.subscribe((project) => {
            this.selectedProject = project;
            this.selectedProjectId = project?.projectId || null;
            
            // Load projects when a project is selected
            if (project && this.projectList.length === 0) {
                this.loadProjects();
            }
        });
    }

    loadProjects(): void {
        this.isLoadingProjects = true;
        this.projectService.getAll(true)
            .pipe(finalize(() => this.isLoadingProjects = false))
            .subscribe({
                next: (projects: ProjectApiModel[]) => {
                    // Sort by projectId descending (most recent first)
                    this.projectList = projects.sort((a, b) => (b.projectId || 0) - (a.projectId || 0));
                },
                error: (error) => {
                    console.error('Error loading projects:', error);
                }
            });
    }

    onProjectChange(event: any): void {
        const selectedProjectId = event.value;
        const project = this.projectList.find(p => p.projectId === selectedProjectId);
        
        if (project) {
            // Update the selected project in the service
            this.userService.setSelectedProjectFromApiModel(project);
            
            // Navigate to the project dashboard
            this.router.navigate([`/project-management/project/dashboard/${project.projectId}`]);
        }
    }

    goToProjectDashboard(): void {
        if (this.selectedProjectId) {
            this.router.navigate([`/project-management/project/dashboard/${this.selectedProjectId}`]);
        }
    }
}
